import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Clock, Linkedin, Twitter, MessageCircle, Link as LinkIcon } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { FINAL_CTA } from "../mock";
import { Reveal, DarkCTA, CtaLink } from "../components/Shared";
import { PostCard, formatDate, fetchPosts } from "./Blog";
import { mediaUrl, API_BASE } from "../api";
import { Seo, articleLd, breadcrumbLd } from "../components/Seo";

const BlogPost = () => {
  const { slug } = useParams();
  const [all, setAll] = useState(null);

  useEffect(() => {
    fetchPosts().then(setAll).catch(() => setAll([]));
  }, []);

  const post = all?.find((p) => p.slug === slug);

  if (all === null) return <div className="min-h-[60vh]" data-testid="post-loading" />;
  if (!post) return <Navigate to="/blog" replace />;

  const related = all.filter((p) => p.slug !== post.slug)
    .sort((a, b) => (a.category === post.category ? -1 : 1) - (b.category === post.category ? -1 : 1))
    .slice(0, 3);

  const shareUrl = `${API_BASE}/api/s/blog/${slug}`;
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  return (
    <>
      <Seo title={post.seoTitle || post.title} description={post.seoDescription || post.excerpt} image={post.ogImage || post.cover} type="article" markdownUrl={`${API_BASE}/api/md/blog/${post.slug}`} jsonLd={[articleLd(post), breadcrumbLd([{ name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }])]} />
      <article>
        {/* header */}
        <header className="pt-12 md:pt-16 pb-10">
          <div className="container-x max-w-[820px]">
            <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--fr-muted)] hover:text-[var(--fr-ink)]" data-testid="post-back">
              <ArrowLeft size={15} /> All articles
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-3 text-sm text-[var(--fr-muted)] fade-up">
              <span className="chip">{post.category}</span>
              <span>{formatDate(post.date)}</span>
              <span className="inline-flex items-center gap-1">
                <Clock size={13} /> {post.readTime}
              </span>
            </div>
            <h1 className="font-display font-bold tracking-tight leading-[1.08] text-3xl md:text-5xl mt-5 fade-up delay-1" data-testid="post-title">
              {post.title}
            </h1>
            <p className="mt-5 text-lg text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">{post.excerpt}</p>
            <div className="mt-6 flex items-center gap-3 fade-up delay-3">
              <span className="w-9 h-9 rounded-full bg-[var(--fr-ink)] text-white text-xs font-bold flex items-center justify-center">F</span>
              <div className="text-sm">
                <div className="font-semibold">{post.author}</div>
                <div className="text-[var(--fr-muted)]">Fleetroot</div>
              </div>
            </div>
          </div>
        </header>

        {/* cover */}
        <div className="container-x max-w-[1000px]">
          <Reveal className="card p-3">
            <div className="img-tile aspect-[21/10]">
              <img src={mediaUrl(post.cover)} alt={post.title} />
            </div>
          </Reveal>
        </div>

        {/* body */}
        <div className="container-x max-w-[820px] py-14 grid lg:grid-cols-[1fr_auto] gap-10">
          <div className="prose-fr" data-testid="post-body">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.content}</ReactMarkdown>
          </div>
          <aside className="lg:sticky lg:top-24 self-start flex lg:flex-col gap-2" aria-label="Share" data-testid="share-bar">
            <a
              className="social-btn"
              href={`https://wa.me/?text=${encodeURIComponent(`${post.title} ${shareUrl}`)}`}
              target="_blank"
              rel="noreferrer"
              title="Share on WhatsApp"
              aria-label="Share on WhatsApp"
              data-testid="share-whatsapp"
            >
              <MessageCircle size={16} />
            </a>
            <a
              className="social-btn"
              href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noreferrer"
              aria-label="Share on LinkedIn"
            >
              <Linkedin size={16} />
            </a>
            <a
              className="social-btn"
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noreferrer"
              aria-label="Share on X"
            >
              <Twitter size={16} />
            </a>
            <button type="button" className="social-btn" onClick={copyLink} aria-label="Copy link" data-testid="post-copy-link">
              <LinkIcon size={16} />
            </button>
          </aside>
        </div>

        {/* inline CTA */}
        <div className="container-x max-w-[820px] pb-16">
          <Reveal className="card p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-5 bg-[var(--fr-primary-tint)]/40 border-[var(--fr-primary-tint)]">
            <div className="flex-1">
              <h3 className="font-display font-bold text-xl">See this in action on your own fleet.</h3>
              <p className="mt-1 text-[15px] text-[var(--fr-ink-2)]">14-day free trial. No credit card. Unlimited riders.</p>
            </div>
            <CtaLink cta={FINAL_CTA.cta} className="btn btn-primary" data-testid="post-inline-cta" />
          </Reveal>
        </div>
      </article>

      {/* related */}
      <section className="section bg-[var(--fr-bg-alt)]" data-testid="post-related">
        <div className="container-x">
          <div className="flex items-end justify-between gap-6">
            <h2 className="h-section">Keep reading</h2>
            <Link to="/blog" className="text-sm font-semibold text-[var(--fr-primary-dark)] hover:underline whitespace-nowrap">
              All articles →
            </Link>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {related.map((p, i) => (
              <PostCard key={p.slug} post={p} delay={i * 70} />
            ))}
          </div>
        </div>
      </section>

      <DarkCTA title={FINAL_CTA.title} text={FINAL_CTA.text} cta={FINAL_CTA.cta} secondary={FINAL_CTA.secondary} email={FINAL_CTA.email} />
    </>
  );
};

export default BlogPost;
